import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { magicQuoteApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, Pagination } from '../components/ui.tsx';
import { QuotesTabs } from '../components/quote/quotes-tabs.tsx';
import AddressLink from '../components/address-link.tsx';
import { useDocumentTitle, useOnlineStatus } from '../hooks/index.ts';
// Aliased: `clearSession` also names the sign-out helper on the session
// context, and the two do very different things to somebody's day.
import { clearSession as clearQueuedCaptures, pendingFor } from '../state/capture-queue.ts';
import { adoptLocalWalkthroughs, listLocalWalkthroughs, removeLocalWalkthrough } from '../state/local-walkthrough.ts';
import { useAiDisclosure } from '../components/ai-disclosure.tsx';
import { isLocalWalkthroughId } from '../state/walkthrough-id.ts';
import { PLANS, minimumPlanFor, planHasFeature } from '@shared/constants/index.ts';

/**
 * The Walkthroughs tab under Quotes: where past Magic Quote walkthroughs are
 * found again, resumed, or deleted.
 *
 * Starting one is not here any more. It lives on the "New quote" page beside
 * the other two ways to begin a quote (pages/new-quote), so that a cleaner
 * learns one place to start work from whichever way they are about to do it.
 * This tab keeps the history — the list the company has on the server and the
 * walks this browser started without signal — and a "Start walkthrough" button
 * that leads to that page with Magic Quote already chosen.
 */
export default function WalkthroughsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('magicQuote.home.documentTitle'));
  const { can, company, currentLocation } = useSession();
  const online = useOnlineStatus();

  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // The walkthrough being asked about, held rather than deleted on the click:
  // this takes the photos and recordings of somebody's home with it, and it is
  // one tap away from Resume.
  const [deleting, setDeleting] = useState<{ id: string; name: string; captureCount: number } | null>(null);

  // Four things have to agree before a cleaner is sent into somebody's house
  // with a camera: the plan carries the feature, the deployment has a model
  // provider, the company has AI on, and the company has this feature on. A
  // walkthrough that can never be read is media collected for nothing.
  //
  // The server answers all four as one field, so this page and the mobile app
  // cannot drift apart on it. The two below only decide WHICH of the four to
  // explain.
  const enabled = company?.magicQuoteAvailable !== false;
  const planGated = company !== null && !planHasFeature(company.effectivePlan, 'magic_quote');
  const configured = company?.aiConfigured !== false;

  // Paged, not "the twenty most recent". A company that has been walking
  // properties for a year has hundreds of these, and a walkthrough is the one
  // record whose photos and recordings expire: being unable to reach the
  // twenty-first means being unable to reach it before retention closes on it.
  const sessions = useQuery({
    queryKey: ['magic-quotes', 'recent', page],
    queryFn: () => magicQuoteApi.list({ page, pageSize: 20 }),
    enabled: can('quotes:read'),
    placeholderData: (previous) => previous,
  });

  /**
   * Walkthroughs this browser started with no signal.
   *
   * `networkMode: 'always'` is not a detail: without it react-query PAUSES a
   * query while the browser is offline, and this one reads IndexedDB — the one
   * source that is guaranteed to answer when there is no network. Paused, the
   * list of walkthroughs saved on this device would be empty in exactly the
   * conditions it exists for.
   */
  const scope = { companyId: company?.id ?? null, locationId: currentLocation?.id ?? null };
  const local = useQuery({
    queryKey: ['magic-quotes', 'local', scope.companyId, scope.locationId],
    // Behind `quotes:read` like the server's list, because it is the same
    // list: customer names, addresses and capture counts for this company's
    // walkthroughs. A user the API withholds those from must not be handed
    // them by the device instead.
    enabled: can('quotes:read'),
    queryFn: async () => {
      const records = await listLocalWalkthroughs(scope);
      return Promise.all(
        records.map(async (record) => ({
          record,
          // What is actually on this phone, which is the number the estimator
          // is deciding about — not a server capture count, because for these
          // there is no server copy yet.
          pending: (await pendingFor(record.localId)).length,
        })),
      );
    },
    networkMode: 'always',
  });

  const queryClient = useQueryClient();
  const disclosure = useAiDisclosure();
  const beforeHandover = useRef(disclosure.ensure);
  beforeHandover.current = disclosure.ensure;

  /**
   * Hands the waiting walkthroughs over as soon as there is something to hand
   * them over on.
   *
   * Here rather than deeper in, because this is the screen a cleaner comes
   * back to: the walk is done, the phone found signal in the driveway, and the
   * expectation is that the walkthrough is simply *there*. The app shell also
   * triggers this, so leaving the page open is not required — this is the
   * screen that has to redraw when it lands.
   */
  useEffect(() => {
    if (!online || !can('quotes:write')) return;
    let current = true;
    void adoptLocalWalkthroughs({
      ...scope,
      isCurrent: () => current,
      beforeRequest: () => beforeHandover.current(),
    }).then((results) => {
      if (results.length === 0) return;
      void queryClient.invalidateQueries({ queryKey: ['magic-quotes'] });
    });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, can, scope.companyId, scope.locationId, queryClient]);

  const remove = useMutation({
    // Otherwise react-query pauses it while offline and the confirmation
    // dialog sits there spinning over a walkthrough that lives on this device
    // and needs nothing from the network to be deleted.
    networkMode: 'always',
    mutationFn: async (id: string) => {
      // A walkthrough that never reached the server is deleted where it
      // actually lives. Asking the API to remove an id it has never heard of
      // would 404, and the estimator would be told their walkthrough could not
      // be deleted while it sat there in front of them.
      if (isLocalWalkthroughId(id)) {
        // The record goes first, and `removeLocalWalkthrough` waits for any
        // handover already in flight before it does. Otherwise the two race
        // over one walkthrough: the handover creates it server-side, re-keys
        // the captures and writes its record back, and a walkthrough somebody
        // just deleted reappears — or worse, uploads.
        //
        // One place decides: `removeLocalWalkthrough` waits for any handover
        // in flight, deletes the record, and reports whether that handover
        // had already landed. Reading the record separately first left a
        // window where adoption could start between the two — creating a
        // server walkthrough that the delete then never heard about.
        //
        // Losing the local row before the server copy is gone is safe in a
        // way the reverse is not: once adopted it is an ordinary walkthrough
        // in the company's own list, with the ordinary way to delete it.
        const { serverId } = await removeLocalWalkthrough(id);
        await clearQueuedCaptures(id);
        if (serverId) {
          await magicQuoteApi.remove(serverId);
          await clearQueuedCaptures(serverId);
        }
        return;
      }
      await magicQuoteApi.remove(id);
      // And the copies still on this device. The confirmation says the photos
      // and recordings are gone, and a walkthrough deleted while some of them
      // were still queued — offline, or backed off after a failure — left
      // those sitting in IndexedDB with nowhere left to send them: the session
      // they belong to no longer exists, so no amount of resuming would ever
      // flush or clear them. Best-effort by construction (`clearSession`
      // swallows storage faults), because a browser that will not open its own
      // database is not a reason to report the deletion as failed.
      await clearQueuedCaptures(id);
    },
    onSuccess: () => {
      setDeleting(null);
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['magic-quotes'] });
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <WorkPage
      title={t('quotes.list.title')}
      description={t('magicQuote.home.description')}
      actions={
        enabled && can('quotes:write') ? (
          <Link to="/quotes/new?method=magic" className="btn btn-primary">
            {t('magicQuote.home.start')}
          </Link>
        ) : null
      }
    >
      {disclosure.dialog}

      <QuotesTabs />

      {/* Unavailable stops new walkthroughs and nothing else. Returning early
          here used to take the history with it, so a company that switched
          the feature off, lost its provider or changed plan could no longer
          FIND the walkthroughs it already has — the sessions and their
          retained media stay perfectly readable through the API, so the only
          way back was a saved URL. Turning off collection is not a request to
          hide the record of what was already collected. */}
      {!enabled ? (
        planGated ? (
          <Alert
            tone="info"
            title={t('magicQuote.home.planTitle', { plan: PLANS[minimumPlanFor('magic_quote')].name })}
          >
            <p>{t('magicQuote.home.planBody', { plan: PLANS[minimumPlanFor('magic_quote')].name })}</p>
            <p className="mt-2">{t('magicQuote.home.planCost')}</p>
          </Alert>
        ) : (
          <Alert tone="info" title={t('magicQuote.home.disabledTitle')}>
            {configured ? t('magicQuote.home.disabledBody') : t('magicQuote.home.unconfiguredBody')}
          </Alert>
        )
      ) : null}

      {error ? (
        <Alert tone="error" title={t('magicQuote.home.deleteErrorTitle')}>
          {error}
        </Alert>
      ) : null}

      {/* Above the server's list, not merged into it, because these are not
          the same kind of thing: one is a record the company has, the other is
          a walk that exists on this browser and nowhere else. Merging them
          would put a row that only this device can see beside rows any
          colleague can open, with nothing to tell them apart. */}
      {(local.data?.length ?? 0) > 0 ? (
        <>
          <h2 className="mb-1 text-lg font-semibold">{t('magicQuote.home.localTitle')}</h2>
          <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.localBody')}</p>
          <ul className="mb-6 flex flex-col gap-2">
            {(local.data ?? []).map(({ record, pending }) => (
              <li
                key={record.localId}
                className="card flex flex-wrap items-center justify-between gap-3 border-[color:var(--color-warning)]/40"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{record.customerName ?? t('magicQuote.home.noCustomer')}</p>
                    <span
                      className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]"
                      role="status"
                    >
                      {t('magicQuote.home.localBadge')}
                    </span>
                  </div>
                  {/* Not a link, unlike the server rows below. What
                      `startHere` stores on a local record is a label — the
                      property's nickname, or its street line on its own —
                      because that is what it can read without a network. It is
                      enough to recognize a row by and not enough to point a map
                      at: "Warehouse" is not a place, and a street with no town
                      exists in hundreds of them. */}
                  {record.propertyAddress ? (
                    <p className="mt-0.5 text-sm whitespace-pre-line text-[color:var(--color-muted)]">
                      {record.propertyAddress}
                    </p>
                  ) : null}
                  <p className="mt-1 text-sm text-[color:var(--color-muted)]">
                    <span className="tabular">{t('magicQuote.home.captureCount', { count: pending })}</span>
                  </p>
                  {/* Only ever set when the server refused the walkthrough on
                      its merits — the feature switched off, the plan changed
                      under it. Retrying is not the remedy and saying nothing
                      leaves a row that never moves, so the reason is shown. */}
                  {record.lastError ? (
                    <p className="mt-1 text-sm text-[color:var(--color-danger)]">{record.lastError}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Link className="btn btn-primary" to={`/magic-quote/${record.localId}`}>
                    {t('magicQuote.home.resume')}
                  </Link>
                  {can('quotes:delete') ? (
                    <Button
                      variant="ghost"
                      className="text-[color:var(--color-danger)]"
                      onClick={() =>
                        setDeleting({
                          id: record.localId,
                          name: record.customerName ?? t('magicQuote.home.noCustomer'),
                          captureCount: pending,
                        })
                      }
                    >
                      {t('common.remove')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h2 className="mb-3 text-lg font-semibold">{t('magicQuote.home.recentTitle')}</h2>
      {/* The server's list is a network read, so offline it is not empty — it
          is unknown, and an empty state saying "no walkthroughs yet" to a
          company with two hundred of them is a lie the software has no reason
          to tell. */}
      {!online ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('magicQuote.home.offlineList')}</p>
      ) : null}
      {sessions.isLoading ? <LoadingBlock label={t('magicQuote.home.loading')} /> : null}
      {sessions.data && sessions.data.items.length === 0 ? (
        <WorkEmpty title={t('magicQuote.home.emptyTitle')}>
          <p>{t('magicQuote.home.emptyBody')}</p>
          {enabled && can('quotes:write') ? (
            <Link to="/quotes/new?method=magic" className="btn btn-primary mt-3 inline-block">
              {t('magicQuote.home.start')}
            </Link>
          ) : null}
        </WorkEmpty>
      ) : null}

      <ul className="flex flex-col gap-2">
        {(sessions.data?.items ?? []).map((session) => (
          <li
            key={session.id}
            // An active walkthrough is not one of a list of records — it is
            // something in progress on somebody's phone, and it should be the
            // first thing the eye lands on here.
            className={[
              'card flex flex-wrap items-center justify-between gap-3 transition-colors hover:bg-[color:var(--color-surface)]',
              session.status === 'active' ? 'border-[color:var(--color-brand)]/40' : '',
            ].join(' ')}
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-medium">{session.customerName ?? t('magicQuote.home.noCustomer')}</p>
                <SessionStatusBadge status={session.status} />
              </div>
              {session.propertyAddress ? (
                <p className="mt-0.5 text-sm whitespace-pre-line text-[color:var(--color-muted)]">
                  <AddressLink address={session.propertyAddress}>{session.propertyAddress}</AddressLink>
                </p>
              ) : null}
              {/* The facts as separate spans rather than one interpolated
                  sentence: they are read by scanning down a column of rows,
                  and a run-on line makes the eye re-parse each one. */}
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-[color:var(--color-muted)]">
                <span className="tabular">{t('magicQuote.home.captureCount', { count: session.captureCount })}</span>
                {session.readinessScore !== null ? (
                  <>
                    <Dot />
                    <span className="tabular">{t('magicQuote.home.readiness', { score: session.readinessScore })}</span>
                  </>
                ) : null}
                {/* What this property actually cost, beside how much of it
                    was captured. The two together are the whole answer to
                    "is this worth running on jobs this size?". */}
                {session.billedCredits > 0 ? (
                  <>
                    <Dot />
                    <span className="tabular">{t('magicQuote.home.credits', { count: session.billedCredits })}</span>
                  </>
                ) : null}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Link
                className={`btn ${session.status === 'active' ? 'btn-primary' : 'btn-secondary'}`}
                to={session.status === 'active' ? `/magic-quote/${session.id}` : `/magic-quote/${session.id}/review`}
              >
                {session.status === 'active' ? t('magicQuote.home.resume') : t('magicQuote.home.open')}
              </Link>
              {can('quotes:delete') ? (
                <Button
                  variant="ghost"
                  className="text-[color:var(--color-danger)]"
                  onClick={() =>
                    setDeleting({
                      id: session.id,
                      name: session.customerName ?? t('magicQuote.home.noCustomer'),
                      captureCount: session.captureCount,
                    })
                  }
                >
                  {t('common.remove')}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      <Pagination page={page} totalPages={sessions.data?.totalPages} onPageChange={setPage} />

      {/* Says what goes, in the terms that matter: this is the only copy of
          those photos and recordings, and the quote it produced is not part
          of the bargain — that stays. */}
      {deleting ? (
        <ConfirmDialog
          title={t('magicQuote.home.deleteTitle', { customer: deleting.name })}
          confirmLabel={t('magicQuote.home.deleteConfirm')}
          confirmVariant="danger"
          busy={remove.isPending}
          confirmDisabled={!can('quotes:delete')}
          // Inside the dialog, because the dialog stays open on a failure and
          // the page-level alert would be explaining itself from behind the
          // backdrop.
          error={remove.isError ? remove.error.message : null}
          onConfirm={() => remove.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
        >
          <p>{t('magicQuote.home.deleteBody', { count: deleting.captureCount })}</p>
        </ConfirmDialog>
      ) : null}
    </WorkPage>
  );
}

/** A separator between facts that is not a character anyone has to read. */
function Dot() {
  return <span className="h-1 w-1 rounded-full bg-[color:var(--color-line)]" aria-hidden="true" />;
}

/**
 * Where a walkthrough stands, as a chip rather than a word in a sentence.
 *
 * Four states, three treatments: `active` is the one with work left in it,
 * `generated` is the one that reached a quote, and the remaining two are
 * finished-or-not in a way nobody needs to act on. The label always says which
 * it is, so the color is reinforcement and never the message (spec §27).
 */
function SessionStatusBadge({ status }: { status: 'active' | 'completed' | 'generated' | 'abandoned' }) {
  const { t } = useTranslation();
  const tone =
    status === 'active'
      ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
      : status === 'generated'
        ? 'border-[color:var(--color-brand)]/40 text-[color:var(--color-brand-strong)]'
        : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]';
  return <span className={`badge ${tone}`}>{t(`magicQuote.status.${status}`)}</span>;
}
